import { IconPhotoOff } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { AttachmentDegraded } from "@/types/AttachmentDrop";

interface AttachmentIncompleteBlockProps {
  degraded?: AttachmentDegraded[] | null;
}

/**
 * Sent-mail detail: attachments that DID reach the guest but shipped without a
 * part — a wallet pass whose logo or strip image could not be bundled.
 *
 * Deliberately its own block rather than a row in {@link AttachmentDropsBlock}:
 * that one is headed "attachments were not delivered with this email", and
 * saying that about a ticket the guest is holding would be worse than the
 * silence this replaces. Until this existed the loss produced a single server
 * log line and nothing else — no drop record, no flag, nothing the sender could
 * ever see, which is how a customer ended up with a pass showing only a QR code.
 */
export const AttachmentIncompleteBlock: React.FC<
  AttachmentIncompleteBlockProps
> = ({ degraded }) => {
  const t = useTranslations("attachmentDropReason");

  if (!degraded || degraded.length === 0) return null;

  // `t.has` fallback: a newer backend may grow codes this build doesn't know.
  const reasonLabel = (code: string) => (t.has(code) ? t(code) : t("unknown"));

  return (
    <div className="flex flex-col gap-2 mb-4 max-h-56 overflow-y-auto">
      <div className="flex items-center gap-1.5">
        <IconPhotoOff size={14} className="shrink-0 font-tertiary" />
        <p className="body-3-medium font-secondary">
          {t("incompleteTitle", { count: degraded.length })}
        </p>
      </div>
      {degraded.map((d, i) => (
        <div
          key={`${d.reason}-${d.item}-${i}`}
          className="border rounded-md px-3 py-2 flex items-center gap-2 surface-secondary outline-quaternary"
        >
          <IconPhotoOff size={14} className="shrink-0 font-tertiary" />
          {/* Name the missing PART, not just the attachment — "logo.png" is
              what the operator has to go fix in the builder. */}
          <span className="body-3-medium truncate font-secondary">
            {d.attachmentName || d.filename}
            {d.item ? ` · ${d.item}` : ""}
          </span>
          <span className="body-3-regular flex-1 min-w-0 truncate font-tertiary">
            {reasonLabel(d.reason)}
          </span>
        </div>
      ))}
    </div>
  );
};
